import { useVideoCreatorStore } from '@src/stores-v2';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { DialogTitle } from '../common/dialogStyles';
import Modal from '../common/Modal';
import { useBRollUpload } from './BRollUploadContext';

import type { FormEvent } from 'react';

type Props = {};

const EmailModal: React.FC<Props> = () => {
  const videoCreator = useVideoCreatorStore();
  const { bRollService } = useBRollUpload();

  const [showContributorModal, setShowContributorModal] = useState(false);

  // Check if contributor information exists
  useEffect(() => {
    const contributor = localStorage.getItem('broll-contributor');
    if (!contributor || contributor === '') {
      setShowContributorModal(true);
      return;
    }

    try {
      const parsedContributor = JSON.parse(contributor);
      if (
        !parsedContributor.name ||
        !parsedContributor.email ||
        !validateEmail(parsedContributor.email)
      ) {
        setShowContributorModal(true);
      } else {
        // Set contributor info in the store
        bRollService.saveContributor({
          name: parsedContributor.name,
          email: parsedContributor.email,
        });
      }
    } catch (error) {
      localStorage.removeItem('broll-contributor');
      setShowContributorModal(true);
    }
  }, []);

  const [contributor, setContributor] = useState<
    Record<'name' | 'email', string>
  >({ name: '', email: '' });

  const validateEmail = (email: string) => {
    const regex = /^[\w-]+(?:\.[\w-]+)*@(?:[\w-]+\.)+[a-zA-Z]{2,}$/;
    return regex.test(email);
  };

  const handleSaveContributor = (
    contributor: Record<'name' | 'email', string>,
  ) => {
    if (validateEmail(contributor.email) && contributor.name) {
      localStorage.setItem('broll-contributor', JSON.stringify(contributor));
      bRollService.saveContributor({
        name: contributor.name,
        email: contributor.email,
      });
      setShowContributorModal(false);
    } else {
      videoCreator.showToast({
        state: 'error',
        message: 'Please enter a valid name and email',
      });
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    handleSaveContributor(contributor);
  };

  return (
    <Modal
      isOpen={showContributorModal}
      backgroundColor="#03041977"
      contentStyle={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <Content>
        <Form onSubmit={handleSubmit}>
          <Heading>Enter your email</Heading>
          <FieldSet>
            <FieldName>Name</FieldName>
            <Input
              type="text"
              required
              placeholder="Please enter your name"
              value={contributor.name}
              onKeyDown={e => e.stopPropagation()}
              onChange={e =>
                setContributor({ ...contributor, name: e.target.value })
              }
            />
          </FieldSet>
          <FieldSet>
            <FieldName>Email</FieldName>
            <Input
              type="email"
              required
              placeholder="Please enter your email"
              value={contributor.email}
              onKeyDown={e => e.stopPropagation()}
              onChange={e =>
                setContributor({ ...contributor, email: e.target.value })
              }
            />
          </FieldSet>
          <SaveButton type="submit">Save</SaveButton>
        </Form>
      </Content>
    </Modal>
  );
};

const Content = styled.div`
  width: 100%;
  background-color: var(--background);
  box-shadow: var(--elevation-toast);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 32px;
  &,
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }
  @media (min-width: 460px) {
    max-width: 460px;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Heading = styled(DialogTitle)``;

const FieldSet = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  text-align: left;
`;

const Input = styled.input`
  border-radius: 8px;
  border: 1px solid var(--border);
  padding: 16px;
  color: var(--text-secondary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-regular);
  line-height: 140%;
  background: var(--background);
`;

const FieldName = styled.div`
  color: var(--text-secondary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-bold);
  line-height: normal;
`;

const SaveButton = styled.button`
  width: 100%;
  height: 48px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  border: none;
  background-color: var(--primary);
  color: var(--background);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-bold);
  cursor: pointer;
  transition: var(--transition-normal) ease;

  &:hover {
    background-color: #14b357;
  }
`;

export default EmailModal;
